// Buyer-shortlist API client for agent client cases. The shortlist is
// the set of candidate properties an agent is shepherding for a buyer;
// it hangs off a client case (case_type='client').
//
// Wire shape mirrors backend/internal/proagent/shortlist.go
// (CaseShortlistItem). adaptShortlistItem reshapes it into the
// ShortlistItem the ClientCase tabs render.

import { apiJson, apiPatchJson, apiPostJson } from "./api";

export type ShortlistStage =
  | "Candidate"
  | "Recommended"
  | "Viewing Scheduled"
  | "Viewed"
  | "Interested"
  | "Offer Considering"
  | "Rejected";

// ShortlistItemWire — the JSON returned by /agent/cases/{id}/shortlist.
export type ShortlistItemWire = {
  id: string;
  case_id: string;
  agent_id: string;
  project_name: string;
  property_kind: string;
  address: string;
  price?: number;
  size_sqft?: number;
  beds: string;
  stage: ShortlistStage;
  why: string;
  risk: string;
  feedback: string;
  next_step: string;
  tags: string[];
  sort_order: number;
  created_at: string;
  updated_at: string;
  // Server-computed (price / size_sqft); absent when either is missing.
  psf?: number;
};

// ShortlistItem — the shape the ClientCase tabs (ShortlistPipeline,
// CompareTable, BuyerReportPreview) render. Moved here from
// agentMockData.ts now that the data is live.
export type ShortlistItem = {
  id: string;
  name: string;
  price: number;
  size: number;
  psf: number;
  why: string;
  risk: string;
  feedback: string;
  next: string;
  col: string;
};

// adaptShortlistItem reshapes a wire row into the FE display type.
// feedback falls back to "—" because ShortlistCard treats "—" as the
// "no feedback yet" sentinel and hides the quote block.
export function adaptShortlistItem(w: ShortlistItemWire): ShortlistItem {
  return {
    id: w.id,
    name: w.project_name,
    price: w.price ?? 0,
    size: w.size_sqft ?? 0,
    psf: w.psf ?? 0,
    why: w.why,
    risk: w.risk,
    feedback: w.feedback.trim() === "" ? "—" : w.feedback,
    next: w.next_step,
    col: w.stage,
  };
}

// CreateShortlistItemInput — payload for addShortlistItem. Matches
// proagent.CaseShortlistInput.
export type CreateShortlistItemInput = {
  project_name: string;
  property_kind?: string;
  address?: string;
  price?: number;
  size_sqft?: number;
  beds?: string;
  stage?: ShortlistStage;
  why?: string;
  tags?: string[];
};

// UpdateShortlistItemInput — partial update; matches
// proagent.CaseShortlistUpdate.
export type UpdateShortlistItemInput = {
  stage?: ShortlistStage;
  why?: string;
  risk?: string;
  feedback?: string;
  next_step?: string;
  sort_order?: number;
};

export async function getCaseShortlist(caseId: string): Promise<ShortlistItem[]> {
  const r = await apiJson<{ items: ShortlistItemWire[] }>(
    `/agent/cases/${encodeURIComponent(caseId)}/shortlist`,
  );
  return (r.items ?? []).map(adaptShortlistItem);
}

export async function addShortlistItem(
  caseId: string,
  input: CreateShortlistItemInput,
): Promise<ShortlistItem> {
  const w = await apiPostJson<ShortlistItemWire>(
    `/agent/cases/${encodeURIComponent(caseId)}/shortlist`,
    input,
  );
  return adaptShortlistItem(w);
}

export async function updateShortlistItem(
  caseId: string,
  itemId: string,
  patch: UpdateShortlistItemInput,
): Promise<ShortlistItem> {
  const w = await apiPatchJson<ShortlistItemWire>(
    `/agent/cases/${encodeURIComponent(caseId)}/shortlist/${encodeURIComponent(itemId)}`,
    patch,
  );
  return adaptShortlistItem(w);
}
